import { FeatureToggle, UpdateFeatureToggleDto, UpdateFeatureToggleOrderDto } from "@/common/api/datasources/Api";
import { api } from "@/common/api/instance";
import { useQuery } from "@tanstack/react-query";

/**
 * Сервіс для роботи з фіча-тоглами
 */
export const featureToggleService = {
    /**
     * Отримати всі доступні фіча-тогли для адміністратора
     */
    getAllFeatureToggles: async (): Promise<FeatureToggle[]> => {
        const response = await api.systemSettings.featureToggleControllerGetAllFeatureToggles();
        return response.data;
    },

    /**
     * Отримати всі фіча-тогли у вигляді об'єкта ключ-значення для фронтенду
     */
    getFeatureTogglesForFrontend: async (): Promise<Record<string, boolean>> => {
        const response = await api.featureToggles.featureTogglePublicControllerGetFeatureTogglesForFrontend();
        return response.data;
    },

    /**
     * Отримати інформацію про конкретний фіча-тогл за ключем
     */
    getFeatureToggle: async (key: string): Promise<FeatureToggle> => {
        const response = await api.systemSettings.featureToggleControllerGetFeatureToggle(key);
        return response.data;
    },

    /**
     * Оновити статус фіча-тогла
     */
    updateFeatureToggle: async (
        key: string,
        data: UpdateFeatureToggleDto
    ): Promise<FeatureToggle> => {
        const response = await api.systemSettings.featureToggleControllerUpdateFeatureToggle(
            key,
            data
        );
        return response.data;
    },

    /**
     * Оновити порядок відображення фіча-тогла
     */
    updateFeatureToggleOrder: async (
        key: string,
        data: UpdateFeatureToggleOrderDto
    ): Promise<FeatureToggle> => {
        const response = await api.systemSettings.featureToggleControllerUpdateFeatureToggleOrder(
            key,
            data
        );
        return response.data;
    },
};

/**
 * Хуки для використання з react-query
 */
export const useFeatureToggleQuery = () => ({
    /**
     * Запит для отримання всіх фіча-тоглів
     */
    allFeatureTogglesQuery: {
        queryKey: ["feature-toggles"],
        queryFn: () => featureToggleService.getAllFeatureToggles(),
    },

    /**
     * Запит для отримання фіча-тоглів для фронтенду
     */
    featureTogglesForFrontendQuery: {
        queryKey: ["feature-toggles-frontend"],
        queryFn: () => featureToggleService.getFeatureTogglesForFrontend(),
    },

    /**
     * Запит для отримання конкретного фіча-тогла
     */
    featureToggleQuery: (key: string) => ({
        queryKey: ["feature-toggle", key],
        queryFn: () => featureToggleService.getFeatureToggle(key),
    }),

    /**
     * Мутація для оновлення фіча-тогла
     */
    updateFeatureToggleMutation: {
        mutationFn: ({ key, data }: { key: string; data: UpdateFeatureToggleDto }) =>
            featureToggleService.updateFeatureToggle(key, data),
    },

    /**
     * Мутація для оновлення порядку фіча-тогла
     */
    updateFeatureToggleOrderMutation: {
        mutationFn: ({ key, data }: { key: string; data: UpdateFeatureToggleOrderDto }) =>
            featureToggleService.updateFeatureToggleOrder(key, data),
    },
});

/**
 * Хук для перевірки, чи ввімкнено певний фіча-тогл
 * @param key Ключ фіча-тогла
 * @param defaultValue Значення за замовчуванням, якщо фіча-тогл не знайдено
 */
export const useFeatureToggle = (key: string, defaultValue = false) => {
    const { data, isLoading, error } = useQuery({
        ...useFeatureToggleQuery().featureTogglesForFrontendQuery,
    });

    const isEnabled = data && data[key] !== undefined
        ? data[key]
        : defaultValue;

    return {
        isEnabled,
        isLoading,
        error,
    };
}; 